import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import type { Job, ModuleRun } from '@/types/forensics';

interface CreateJobResult {
  jobId: string;
  status: string;
}

// Max size for inline base64 upload (10 MB raw = ~13.3 MB base64)
const INLINE_UPLOAD_LIMIT = 10 * 1024 * 1024;

export function useCreateJob() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createJob = useCallback(async (file: File, mode: 'standard' | 'deep', autoDelete: boolean): Promise<CreateJobResult> => {
    setLoading(true);
    setError(null);
    try {
      const isSmall = file.size <= INLINE_UPLOAD_LIMIT;

      // Step 1: Create job — include base64 data only for small files
      let imageData: string | undefined;
      if (isSmall) {
        const arrayBuffer = await file.arrayBuffer();
        imageData = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
      }

      const createResponse = await apiFetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          mode,
          autoDelete,
          ...(isSmall ? { imageData } : {}),
        }),
      });
      if (!createResponse.ok) throw new Error('Failed to create job');
      const { jobId } = await createResponse.json();

      // Step 2: Upload file as binary for large files
      if (!isSmall) {
        const uploadResponse = await apiFetch(`/api/jobs/${jobId}/upload`, {
          method: 'POST',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!uploadResponse.ok) throw new Error('Failed to upload file');
      }

      return { jobId, status: 'queued' };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createJob, loading, error };
}

export function useStartAnalysis() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startAnalysis = useCallback(async (jobId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/jobs/${jobId}/start`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to start analysis');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { startAnalysis, loading, error };
}

export function useJob(jobId: string | undefined) {
  const [job, setJob] = useState<Job | null>(null);
  const [moduleRuns, setModuleRuns] = useState<ModuleRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/jobs/${jobId}`);
      if (!response.ok) throw new Error('Failed to fetch job');
      const data = await response.json();
      setJob(data);
      setModuleRuns(data.moduleRuns || []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  return { job, moduleRuns, loading, error, fetchJob };
}
